import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import DashboardHeader from "@/components/DashboardHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Check, X, Loader2, Users, UserCheck, UserX, Clock, Sparkles } from "lucide-react";
import { toast } from "sonner";

function StatBox({ label, value, icon: Icon, accent }) {
  return (
    <Card className="border-stone-200">
      <CardContent className="py-5 flex items-center gap-4">
        <div className={`h-10 w-10 rounded-md flex items-center justify-center ${accent}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-2xl font-semibold tracking-tight" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            {value}
          </div>
          <div className="text-xs text-stone-500">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function UserRow({ u, onApprove, onReject, busyId }) {
  const isBusy = busyId === u.id;
  const statusBadge = {
    pending: "bg-amber-50 text-amber-900 border-amber-300",
    approved: "bg-emerald-50 text-emerald-900 border-emerald-300",
    rejected: "bg-red-50 text-red-900 border-red-300",
  }[u.status];
  return (
    <div
      className="flex items-center justify-between gap-3 px-4 py-3 border-b border-stone-100 last:border-b-0"
      data-testid={`user-row-${u.email}`}
    >
      <div className="min-w-0">
        <div className="text-sm font-medium text-stone-900 truncate">{u.name}</div>
        <div className="text-xs text-stone-500 truncate">{u.email}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Badge variant="outline" className="capitalize">{u.role}</Badge>
        <Badge variant="outline" className={statusBadge}>{u.status}</Badge>
        {u.status === "pending" && onApprove && (
          <>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={isBusy}
                  data-testid={`approve-button-${u.email}`}
                >
                  <Check className="mr-1 h-3.5 w-3.5" /> Approve
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Approve {u.name}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will give them {u.role} access to KKS.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => onApprove(u.id)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`approve-confirm-${u.email}`}
                  >
                    Approve
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  disabled={isBusy}
                  data-testid={`reject-button-${u.email}`}
                >
                  <X className="mr-1 h-3.5 w-3.5" /> Reject
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reject {u.name}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    They will be blocked from accessing KKS.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => onReject(u.id)}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid={`reject-confirm-${u.email}`}
                  >
                    Reject
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [all, setAll] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [aiCourseOpen, setAiCourseOpen] = useState(false);
  const [aiTeacherId, setAiTeacherId] = useState("");

  const approvedTeachers = useMemo(
    () =>
      all.filter((u) => u.role === "teacher" && u.status === "approved"),
    [all]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a, s] = await Promise.all([
        api.get("/admin/users", { params: { status: "pending" } }),
        api.get("/admin/users"),
        api.get("/admin/stats"),
      ]);
      setPending(p.data);
      setAll(a.data);
      setStats(s.data);
    } catch (err) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/users/${id}/approve`);
      toast.success("User approved");
      await load();
    } catch {
      toast.error("Approve failed");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/users/${id}/reject`);
      toast.success("User rejected");
      await load();
    } catch {
      toast.error("Reject failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="admin-dashboard">
      <DashboardHeader subtitle="Admin · Approvals & users" />

      <main className="mx-auto max-w-6xl px-6 py-8 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Admin dashboard</h1>
            <p className="text-sm text-stone-500">Approve teachers and students, browse all users.</p>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/admin/grading"
              className="text-sm text-stone-900 underline hover:no-underline"
              data-testid="admin-grading-link"
            >
              Grading overview →
            </Link>
            <Link
              to="/admin/payment-settings"
              className="text-sm text-stone-900 underline hover:no-underline"
              data-testid="admin-payment-settings-link"
            >
              Payment settings →
            </Link>
            <Link
              to="/admin/enrollments"
              className="text-sm text-stone-900 underline hover:no-underline"
              data-testid="admin-enrollments-link"
            >
              Enrollments →
            </Link>
            <Link
              to="/admin/referrals"
              className="text-sm text-stone-900 underline hover:no-underline"
              data-testid="admin-referrals-link"
            >
              Referral analytics →
            </Link>
            <Link
              to="/admin/sessions"
              className="text-sm text-stone-900 underline hover:no-underline"
              data-testid="admin-sessions-link"
            >
              All sessions →
            </Link>
            <Link
              to="/admin/courses"
              className="text-sm text-stone-900 underline hover:no-underline"
              data-testid="admin-courses-link"
            >
              View all courses →
            </Link>
          </div>
        </div>

        {/* Phase-fix: Admin AI Course CTA */}
        <button
          type="button"
          onClick={() => {
            setAiTeacherId("");
            setAiCourseOpen(true);
          }}
          className="block w-full text-left"
          data-testid="admin-ai-course-cta"
        >
          <Card className="border-amber-300 bg-gradient-to-r from-amber-50 via-amber-100/60 to-amber-50 hover:shadow-lg transition-all hover:-translate-y-0.5 cursor-pointer">
            <CardContent className="py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="rounded-xl bg-amber-500 p-3 text-white shrink-0">
                  <Sparkles className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-lg font-semibold text-stone-900">
                    ✨ Create a course on behalf of a teacher
                  </div>
                  <p className="text-sm text-stone-700 mt-1">
                    Open the AI wizard — pick which approved teacher will own the
                    generated course.
                  </p>
                </div>
              </div>
              <Button
                size="lg"
                className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  setAiTeacherId("");
                  setAiCourseOpen(true);
                }}
              >
                Pick a teacher →
              </Button>
            </CardContent>
          </Card>
        </button>

        <Dialog open={aiCourseOpen} onOpenChange={setAiCourseOpen}>
          <DialogContent data-testid="admin-ai-course-dialog">
            <DialogHeader>
              <DialogTitle>Create AI course as teacher</DialogTitle>
              <DialogDescription>
                The generated course will be owned by the selected teacher and
                will appear in their dashboard.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Select value={aiTeacherId} onValueChange={setAiTeacherId}>
                <SelectTrigger data-testid="admin-ai-teacher-select">
                  <SelectValue placeholder="Choose an approved teacher…" />
                </SelectTrigger>
                <SelectContent>
                  {approvedTeachers.length === 0 ? (
                    <SelectItem value="__none__" disabled>
                      No approved teachers yet
                    </SelectItem>
                  ) : (
                    approvedTeachers.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} ({t.email})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setAiCourseOpen(false)}
                data-testid="admin-ai-cancel"
              >
                Cancel
              </Button>
              <Button
                disabled={!aiTeacherId}
                onClick={() => {
                  setAiCourseOpen(false);
                  navigate(
                    `/teacher/courses/new?as_teacher=${aiTeacherId}`
                  );
                }}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="admin-ai-continue"
              >
                Continue to wizard →
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {stats && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-stats">
            <StatBox
              label="Pending teachers"
              value={stats.pending_teachers}
              icon={Clock}
              accent="bg-amber-100 text-amber-700"
            />
            <StatBox
              label="Pending students"
              value={stats.pending_students}
              icon={Clock}
              accent="bg-amber-100 text-amber-700"
            />
            <StatBox
              label="Approved teachers"
              value={stats.approved_teachers}
              icon={UserCheck}
              accent="bg-emerald-100 text-emerald-700"
            />
            <StatBox
              label="Approved students"
              value={stats.approved_students}
              icon={UserCheck}
              accent="bg-emerald-100 text-emerald-700"
            />
          </div>
        )}

        <Tabs defaultValue="pending" className="w-full">
          <TabsList>
            <TabsTrigger value="pending" data-testid="tab-pending">
              Pending approvals ({pending.length})
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">All users ({all.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            <Card className="border-stone-200">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4 text-stone-500" /> Awaiting your decision
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center text-stone-500">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin mb-2" /> Loading…
                  </div>
                ) : pending.length === 0 ? (
                  <div className="p-8 text-center text-stone-500" data-testid="no-pending-message">
                    No pending approvals. You&apos;re all caught up.
                  </div>
                ) : (
                  pending.map((u) => (
                    <UserRow
                      key={u.id}
                      u={u}
                      onApprove={approve}
                      onReject={reject}
                      busyId={busyId}
                    />
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="all">
            <Card className="border-stone-200">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <UserX className="h-4 w-4 text-stone-500" /> All users
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center text-stone-500">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin mb-2" /> Loading…
                  </div>
                ) : (
                  all.map((u) => (
                    <UserRow
                      key={u.id}
                      u={u}
                      onApprove={u.role !== "admin" ? approve : null}
                      onReject={u.role !== "admin" ? reject : null}
                      busyId={busyId}
                    />
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
